/**
 * `<caos-workspace-tabs>` — the app-scoped workspace tab row (shell region `a`),
 * reconstructing the Dragon OS `WorkspaceTabs` layout recolored to the Atlantis
 * `--caos-*` tokens.
 * (Catalog: Navigation › WorkspaceTabs — the app-scoped tab row, with the open records beneath it.)
 *
 * Dragon's model (UI-306/307): the row reads
 *   `[App Launcher] [Atlantis AI anchor] | [the active app's inline object nav]`.
 * The **App Launcher** leads (projected into the `launcher` slot — the host holds no
 * launcher logic), then a persistent **Atlantis AI anchor** (Dragon's "Dragon AI" — a
 * GLOBAL Home that does NOT change the active app), then — inline, scrolling — the
 * **active app's tabs**. "Which app am I on" reads off the launcher's inline lockup; the
 * active tab reads via the accent underline.
 *
 * This replaces the old flat strip (Home + every object + Reports/Dashboards/Setup crammed
 * together): the tabs here are ALREADY app-scoped by `/api/nav`, so switching apps swaps the
 * whole set. Reports/Dashboards are the Atlantis AI app's own tabs (passed in `tabs`); Setup
 * is reached from the launcher + the top-bar gear, never a tab — so nothing is a dead end.
 *
 * Look (Dragon WorkspaceTabs / the restyled tab-strip idiom): a WHITE band in light mode
 * (`--caos-color-surface`, the data-surface white — distinct from the eggshell page/chrome),
 * mono 11px .12em uppercase tabs, active = a 2px accent bottom-border + neutral-ink weight 700
 * (accent underline, NOT accent ink). The launcher stays pinned; only the tab set scrolls.
 *
 * Data in:
 *   `tabs` PROPERTY — `{ id, label, href, active? }[]` (an array, so a property not an
 *          attribute): the active app's ordered destinations. Each carries the route it
 *          navigates to; the host emits, it does not route.
 *   `home-active` attribute (presence boolean) — the home landing is the focused surface, so the
 *          anchor reads active.
 *   `home-label` attribute — the anchor's label (default "Home"). A shell whose home IS an app
 *          passes that app's name; the row never names a package on its own (CAOS-769).
 *   `homeTabs` PROPERTY — `{ id, label, href, icon?, active? }[]` (CAOS-768): destinations PINNED
 *          into the row, drawn between the anchor and the app's tabs and unchanged when the app
 *          changes. The app's own `tabs` are swapped wholesale on every app switch; these are not.
 *          The component does not decide who gets one or how many there may be — it draws the list
 *          it is handed, exactly as it does for `tabs`.
 *
 * Events out (bubbling + composed — the host has no navigation logic):
 *   `workspace-home`     — the Atlantis AI anchor was chosen (navigate Home; do NOT change app).
 *   `workspace-navigate` — detail `{ href, id }`, an app tab was chosen.
 *
 * A11y: `role="tablist"` over the anchor + tabs with a roving tabindex (only the active one is
 * tabbable; Arrow Left/Right/Home/End move + activate), the WAI-ARIA tabs pattern the sibling
 * `<caos-tab-strip>` uses. The launcher sits OUTSIDE the tablist (it is a menu button, not a tab).
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { anchorPanel } from '../anchored-panel.js';
import { applyAccessibleName } from '../accessible-name.js';
import { makeIcon, isIconName } from './icons.js';

/**
  * CAOS-768 — one PINNED destination in the row: present whichever app is open.
  *
  * Deliberately a narrower shape than {@link CaosWorkspaceTab}, not the same one. A pinned tab has
  * no `object`, so no `caret` and no list-view menu — it is a destination, not a window onto one
  * object — and no `overflow`, because a tab that collapses into a "More" menu the moment the bar
  * is tight is not permanently visible, which is the only thing this kind of tab promises.
  */
export interface CaosWorkspaceHomeTab {
  /** A stable id for the tab — the contributing package's key. Carried on the navigate event. */
  id: string;
  /** The word the tab shows. */
  label: string;
  /** The route the tab navigates to (emitted, not followed here). */
  href: string;
  /** A leading glyph by name from the shared icon vocabulary; an unknown name draws none. */
  icon?: string;
  /** Whether this tab is the active surface. */
  active?: boolean;
}

export interface CaosWorkspaceTab {
  /** A stable id for the tab (e.g. `o:account`, `reports`). */
  id: string;
  /** The uppercase-rendered label. */
  label: string;
  /** The route the tab navigates to (emitted, not followed here). */
  href: string;
  /** Whether this tab is the active surface. */
  active?: boolean;
  /**
   * CAOS-507 — open this destination beside the current workspace in a new browser tab. The
   * component emits a distinct event so the host can open it without navigating this document.
   */
  openInNewTab?: boolean;
  /** The object key this tab represents (object tabs only) — carried on the `tab-caret` event so the
   *  host can resolve the object's list views + recents. */
  object?: string;
  /** When true, render the per-tab dropdown caret (the Dragon `ObjectNavCaret` affordance): a small
   *  chevron beside the tab that emits `tab-caret` for the host's per-object menu. */
  caret?: boolean;
  /** The leading glyph the tab carries, by name from the shared icon vocabulary (CAOS-344). An
   *  unknown or absent name renders no glyph rather than a blank box. */
  icon?: string;
  /** When true, this tab is NOT pinned in the bar — it lives in the trailing "More" overflow menu
   *  (CAOS-297/298). Absent/false ⇒ pinned in the bar (today's behaviour). The host computes this from
   *  the app's declared `overflowAfter`; the component just renders whatever split it is handed. */
  overflow?: boolean;
}

const sheet = makeSheet(`
  :host {
    display: flex;
    align-items: stretch;
    /* Dragon's band is WHITE in light mode (the page/chrome is eggshell); the data-surface
       token is that white and flips to the raised dark surface in dark mode. */
    background: var(--caos-color-surface);
    border-bottom: 1px solid var(--caos-color-border);
    /* CAOS-377 — 41px including its hairline. With the bar above it that puts the top of the content
       region where the reference puts it, which is where a console's rail has to start. */
    min-height: 41px;
    min-width: 0;
  }

  /* The launcher projects in here and stays pinned (never scrolls with the tab set). */
  .launcher {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  /* The CURRENT APP name — sits right after the launcher waffle, before the pipe divider,
     so you always know which app you're on (Dragon parity: the launcher lockup). Clean text
     (no icon) per the Salesforce-clean idiom. Hidden when there is no active app. */
  .appname {
    flex-shrink: 0;
    align-self: center;
    margin-left: var(--caos-space-3);
    /* The current-app name reads as a TITLE — the Salesforce hierarchy: a larger DISPLAY-face
       name (distinct from the small mono uppercase tabs) so the app you're on has clear visual
       primacy over its tabs. Title-case, tight tracking. */
    font-family: var(--caos-font-display);
    font-size: 17px;
    font-weight: 700;
    letter-spacing: var(--caos-tracking-tight);
    color: var(--caos-color-text);
    white-space: nowrap;
  }
  .appname[hidden] { display: none; }

  /* A quiet vertical rule echoing Dragon's [launcher] [current app] | [AI][object nav] separator. */
  .divider {
    flex-shrink: 0;
    align-self: center;
    width: 1px;
    height: 20px;
    margin: 0 var(--caos-space-2);
    background: var(--caos-color-border);
  }

  /* The trailing custom-navigation gadget projects into this slot at the far end of the row,
     pinned (never scrolls with the tab set). */
  .gadget {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    align-self: center;
    padding-right: var(--caos-space-2);
  }

  .tablist {
    display: flex;
    align-items: stretch;
    gap: 2px;
    min-width: 0;
    flex: 1 1 auto;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .tablist::-webkit-scrollbar { display: none; }

  .tab {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    /* Dragon WorkspaceTabs — mono, 11px, .08em, mixed-case ("Dragon AI", not "DRAGON AI") */
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    /* The active-tab indicator: a 3px accent underline overlapping the 1px band rule. The accent
       is the CAOS sea-green, which reads lighter than the gold standard's saturated red; at an
       identical 2px both compute the same but the teal renders visibly thinner, so the active
       underline carries one extra pixel of weight to match the gold standard's PERCEIVED weight.
       All tab variants (.tab / .tabwrap / .more) reserve the same 3px so nothing shifts. */
    border-bottom: 3px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 80ms ease, border-color 80ms ease, background 80ms ease;
  }
  /* CAOS-344 — the leading object glyph, sized to the mono label it sits beside. */
  .tab-glyph { width: 14px; height: 14px; flex-shrink: 0; display: block; }
  /* An OBJECT tab is set uppercase (the reference's treatment for the things a workspace holds);
     an app-level entry keeps its mixed case ("Atlantis AI", not "ATLANTIS AI"). */
  .tab.is-object .tab-label { text-transform: uppercase; }
  .tab:hover:not([aria-selected="true"]) { color: var(--caos-color-text); }
  .tab[aria-selected="true"] {
    /* active = neutral ink weight 700 + accent underline (not accent ink) */
    color: var(--caos-color-text);
    font-weight: 700;
    border-bottom-color: var(--caos-color-accent);
    background: var(--caos-color-bg);
  }
  .tab:focus-visible {
    outline: none;
    border-radius: var(--caos-radius-sm);
    box-shadow: var(--caos-focus-ring);
  }

  /* An object tab + its dropdown caret read as ONE unit — the tab label button sits flush against a
     small caret button (Dragon's ObjectNavCaret idiom). The wrapper carries the active band + underline
     across both so the caret inherits the tab's active tint. */
  .tabwrap {
    display: inline-flex;
    align-items: stretch;
    flex-shrink: 0;
    border-bottom: 3px solid transparent;
    margin-bottom: -1px;
  }
  .tabwrap .tab { border-bottom: none; margin-bottom: 0; padding-right: 4px; }
  .tabwrap.is-active { border-bottom-color: var(--caos-color-accent); background: var(--caos-color-bg); }
  .tabwrap.is-active .tab { background: transparent; }

  .tab-caret {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    display: inline-flex;
    align-items: center;
    padding: 0 6px 0 0;
    cursor: pointer;
    transition: color 80ms ease;
  }
  .tabwrap.is-active .tab-caret { color: var(--caos-color-text); }
  .tab-caret:hover { color: var(--caos-color-text); }
  .tab-caret:focus-visible { outline: none; border-radius: var(--caos-radius-sm); box-shadow: var(--caos-focus-ring); }
  .tab-caret svg { width: 12px; height: 12px; display: block; }

  /* The persistent Atlantis AI anchor — the same tab idiom, always the leading tab. */
  .anchor { flex-shrink: 0; }

  /* ── Overflow "More" menu (CAOS-297/298) ──────────────────────────────────────
     The trailing disclosure button that opens the overflow menu. Same tab idiom (mono, uppercase-ish
     mono, accent underline when one of its children is the active surface) so it reads as part of the
     row, not a foreign control. Only rendered when the app declares an overflow split with tabs to
     collapse — no split ⇒ this never appears and the bar is byte-for-byte today's bar. */
  .more {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 12px;
    flex-shrink: 0;
    border-bottom: 3px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 80ms ease, border-color 80ms ease, background 80ms ease;
  }
  .more:hover { color: var(--caos-color-text); }
  .more[aria-expanded="true"] { color: var(--caos-color-text); }
  /* When the active surface lives inside the overflow set, the More button carries the active tint +
     underline, mirroring how a pinned active tab reads. */
  .more.is-active {
    color: var(--caos-color-text);
    font-weight: 700;
    border-bottom-color: var(--caos-color-accent);
    background: var(--caos-color-bg);
  }
  .more:focus-visible {
    outline: none;
    border-radius: var(--caos-radius-sm);
    box-shadow: var(--caos-focus-ring);
  }
  .more svg { width: 12px; height: 12px; display: block; }

  /* The overflow menu popover — position:fixed so the tablist's overflow-x clip never truncates it.
     Placed against the More button's live screen rect in openMenu(). */
  .more-menu {
    position: fixed;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown);
    min-width: 176px;
    padding: var(--caos-space-1);
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    /* CAOS-652 — the default radius and the float shadow reserved for menus. --caos-radius-md and
       --caos-shadow-md were never defined, so the menu drew square with a fallback shadow. */
    border-radius: var(--caos-radius);
    box-shadow: var(--caos-shadow-float);
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .more-menu[hidden] { display: none; }
  .more-item {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text);
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    line-height: 1;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    white-space: nowrap;
    transition: background 80ms ease, color 80ms ease;
  }
  .more-item:hover { background: var(--caos-color-bg); }
  .more-item:focus-visible {
    outline: none;
    background: var(--caos-color-bg);
    box-shadow: var(--caos-focus-ring);
  }
  /* The active overflow item gets a leading accent dot + neutral-ink weight, so the active surface is
     legible when the menu is open. */
  .more-item[aria-current="true"] { font-weight: 700; }
  .more-item .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: transparent;
  }
  .more-item[aria-current="true"] .dot { background: var(--caos-color-accent); }
`);

export class CaosWorkspaceTabs extends CaosElement {
  static get observedAttributes(): string[] {
    return ['home-active', 'home-label', 'active-app-label', 'aria-label'];
  }

  private tablist: HTMLDivElement;
  private anchor: HTMLButtonElement;
  private appName: HTMLSpanElement;
  private _tabs: CaosWorkspaceTab[] = [];
  private _homeTabs: CaosWorkspaceHomeTab[] = [];
  private tabEls: HTMLButtonElement[] = [];
  /** CAOS-768 — the pinned tabs' buttons, in row order, for the roving-tabindex set. */
  private homeTabEls: HTMLButtonElement[] = [];
  /** Top-level nodes appended after the anchor (a bare tab button, or a `.tabwrap` when the tab has a
   *  caret) — tracked so a re-render removes exactly what it added. */
  private tabNodes: HTMLElement[] = [];
  /** The trailing "More" overflow disclosure button + its popover menu (CAOS-297/298), present only
   *  when the app hands over one or more `overflow` tabs. Both null when there is no overflow set — so
   *  an app that declares no split renders exactly today's bar. */
  private moreBtn: HTMLButtonElement | null = null;
  private moreMenu: HTMLDivElement | null = null;
  private menuItemEls: HTMLButtonElement[] = [];
  private menuOpen = false;
  /** Document/window listeners installed only while the overflow menu is open (light-dismiss). */
  private readonly onDocPointer = (e: Event): void => {
    const path = e.composedPath();
    if (this.moreBtn && path.includes(this.moreBtn)) return;
    if (this.moreMenu && path.includes(this.moreMenu)) return;
    this.closeMenu(false);
  };
  private readonly onWinDismiss = (): void => this.closeMenu(false);
  /** Re-render when the root's brand generation changes — a brand swap repaints tokens the display
   *  face reads, so the strip must re-render for them to take live. The home anchor's own label no
   *  longer comes from a token (CAOS-769); it is the `home-label` attribute or "Home". */
  private brandObserver: MutationObserver | null = null;

  constructor() {
    super(sheet);

    // Leading slot — the App Launcher projects in and stays pinned.
    const launcherSlot = document.createElement('slot');
    launcherSlot.name = 'launcher';
    const launcherWrap = document.createElement('span');
    launcherWrap.className = 'launcher';
    launcherWrap.append(launcherSlot);

    // The current-app name — set from the `active-app-label` attribute in render().
    this.appName = document.createElement('span');
    this.appName.className = 'appname';
    this.appName.hidden = true;

    const divider = document.createElement('span');
    divider.className = 'divider';
    divider.setAttribute('aria-hidden', 'true');

    // Trailing slot — the custom-navigation gadget projects in and stays pinned at the far end.
    const gadgetSlot = document.createElement('slot');
    gadgetSlot.name = 'gadget';
    const gadgetWrap = document.createElement('span');
    gadgetWrap.className = 'gadget';
    gadgetWrap.append(gadgetSlot);

    this.tablist = document.createElement('div');
    this.tablist.className = 'tablist';
    this.tablist.setAttribute('part', 'tablist');
    this.tablist.setAttribute('role', 'tablist');
    this.tablist.addEventListener('keydown', (e) => this.onKeydown(e));

    // The persistent Atlantis AI anchor — the global Home (does NOT change the active app).
    this.anchor = document.createElement('button');
    this.anchor.type = 'button';
    this.anchor.className = 'tab anchor';
    this.anchor.setAttribute('part', 'tab');
    this.anchor.setAttribute('role', 'tab');
    this.anchor.addEventListener('click', () => {
      this.emit('workspace-home');
    });
    this.tablist.append(this.anchor);

    this.root.append(launcherWrap, this.appName, divider, this.tablist, gadgetWrap);
  }

  connectedCallback(): void {
    this.syncName();
    this.brandObserver = new MutationObserver(() => this.render());
    this.brandObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-brand'] });
    this.render();
  }

  disconnectedCallback(): void {
    this.brandObserver?.disconnect();
    this.brandObserver = null;
    this.closeMenu(false);
  }

  attributeChangedCallback(name: string): void {
    // CAOS-648 — the name follows the host's `aria-label` whenever it changes.
    if (name === 'aria-label') {
      this.syncName();
      return;
    }
    this.render();
  }

  private syncName(): void {
    applyAccessibleName(this.tablist, this.getAttribute('aria-label'), 'Workspace tabs');
  }

  /** The active app's tabs (data in — an array, so a property not an attribute). */
  get tabs(): CaosWorkspaceTab[] {
    return this._tabs;
  }
  set tabs(list: CaosWorkspaceTab[]) {
    this._tabs = Array.isArray(list) ? list : [];
    this.render();
  }

  /** CAOS-768 — the pinned tabs (data in — an array, so a property not an attribute). */
  get homeTabs(): CaosWorkspaceHomeTab[] {
    return this._homeTabs;
  }
  set homeTabs(list: CaosWorkspaceHomeTab[]) {
    this._homeTabs = Array.isArray(list) ? list : [];
    this.render();
  }

  private get homeActive(): boolean {
    return this.hasAttribute('home-active');
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }

  /** CAOS-769 — the leading anchor is the PLATFORM's home, not any app's. It used to resolve from
   *  `--brand-ai-app-name`, which is the AI app's name, so an org running Core alone showed a
   *  permanent "Atlantis AI" anchor for a package it had not installed. The anchor now names the
   *  home it actually opens: an explicit `home-label` wins — that is how a shell that HAS an AI
   *  home app names it — and absent one it is simply "Home". Nothing here may name a package;
   *  which apps exist is the host's knowledge, and it hands it over through the attribute. */
  private resolveHomeLabel(): string {
    const attr = this.getAttribute('home-label')?.trim();
    return attr || 'Home';
  }

  private render(): void {
    // The current-app name (after the launcher, before the pipe divider) — so it's always clear
    // which app is open. Hidden when unset.
    const appLabel = this.getAttribute('active-app-label')?.trim() ?? '';
    this.appName.textContent = appLabel;
    this.appName.hidden = appLabel.length === 0;

    // The anchor (leading, persistent).
    const homeLabel = this.resolveHomeLabel();
    this.anchor.textContent = homeLabel;
    this.anchor.title = `${homeLabel} — Home`;
    this.anchor.setAttribute('aria-selected', String(this.homeActive));

    // A re-render replaces the tab set, so tear down any open overflow menu first (its DOM is rebuilt).
    this.closeMenu(false);

    // Rebuild the app tab set after the anchor. Split the handed tabs into PINNED (rendered inline in
    // the bar) and OVERFLOW (collapsed into the trailing "More" menu). An app that declares no split
    // hands over zero overflow tabs, so `overflowTabs` is empty and the bar is identical to today.
    for (const el of this.tabNodes) el.remove();
    this.tabNodes = [];
    // The overflow menu lives in the shadow root (not the tablist), so remove it explicitly.
    this.moreMenu?.remove();
    this.moreBtn = null;
    this.moreMenu = null;
    this.menuItemEls = [];

    // CAOS-768 — the pinned tabs come first, immediately after the anchor and before whatever app
    // is open. They are appended here rather than merged into `_tabs` so nothing about them can be
    // reached by the app-tab paths: no overflow split can collapse one into the More menu, and the
    // custom-navigation gadget (which edits the app's OWN tabs) never sees them as its own.
    this.homeTabEls = this._homeTabs.map((t) => this.buildHomeTab(t));

    const pinnedTabs = this._tabs.filter((t) => !t.overflow);
    const overflowTabs = this._tabs.filter((t) => t.overflow);

    this.tabEls = pinnedTabs.map((t) => this.buildTab(t));

    if (overflowTabs.length > 0) this.buildOverflow(overflowTabs);

    this.reflectRovingTabindex();
  }

  /**
   * CAOS-768 — build one PINNED tab, append it to the tablist, and return it for the roving set.
   *
   * It emits the same `workspace-navigate` an app tab emits, carrying the same detail shape, so the
   * host routes it through the path it already has rather than growing a second one. What makes it
   * a different kind of tab is where it is drawn and that it survives an app switch — not how it is
   * chosen.
   */
  private buildHomeTab(t: CaosWorkspaceHomeTab): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tab home-tab';
    btn.setAttribute('part', 'tab');
    btn.setAttribute('role', 'tab');
    btn.dataset.id = t.id;
    if (t.icon && isIconName(t.icon)) {
      const glyph = makeIcon(t.icon);
      glyph.classList.add('tab-glyph');
      btn.append(glyph);
    }
    const text = document.createElement('span');
    text.className = 'tab-label';
    text.textContent = t.label;
    btn.append(text);
    btn.setAttribute('aria-selected', String(Boolean(t.active)));
    btn.addEventListener('click', () => {
      this.emit('workspace-navigate', { href: t.href, id: t.id });
    });
    this.tablist.append(btn);
    this.tabNodes.push(btn);
    return btn;
  }

  /** Build one pinned tab button (with the ObjectNavCaret wrapper when it carries a caret), append it
   *  to the tablist, and return the tab button for the roving-tabindex set. */
  private buildTab(t: CaosWorkspaceTab): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tab';
    btn.setAttribute('part', 'tab');
    btn.setAttribute('role', 'tab');
    btn.dataset.id = t.id;
    // CAOS-344 — an object tab leads with its glyph and sets its label in the reference's uppercase
    // treatment; an app-level entry (the AI home) keeps its mixed case, exactly as the reference does.
    if (t.icon && isIconName(t.icon)) {
      const glyph = makeIcon(t.icon);
      glyph.classList.add('tab-glyph');
      btn.append(glyph);
    }
    const text = document.createElement('span');
    text.className = 'tab-label';
    text.textContent = t.label;
    btn.append(text);
    if (t.object) btn.classList.add('is-object');
    btn.setAttribute('aria-selected', String(Boolean(t.active)));
    btn.addEventListener('click', () => {
      this.emit(t.openInNewTab ? 'workspace-open-new-tab' : 'workspace-navigate', {
        href: t.href,
        id: t.id,
      });
    });

    // Object tabs carry a dropdown caret (Dragon's ObjectNavCaret): the tab + caret ride a wrapper
    // so the active band/underline spans both; the caret emits `tab-caret` for the host's per-object
    // menu (recents + list views), anchored at the caret's live screen rect.
    if (t.caret) {
      const wrap = document.createElement('span');
      wrap.className = t.active ? 'tabwrap is-active' : 'tabwrap';
      const caret = document.createElement('button');
      caret.type = 'button';
      caret.className = 'tab-caret';
      caret.tabIndex = -1;
      caret.setAttribute('aria-haspopup', 'menu');
      caret.setAttribute('aria-label', `${t.label} list views`);
      caret.title = `${t.label} list views`;
      caret.append(makeIcon('chevron'));
      caret.addEventListener('click', (e) => {
        e.stopPropagation();
        const rect = caret.getBoundingClientRect();
        this.emit('tab-caret', {
          id: t.id,
          object: t.object,
          anchor: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
        });
      });
      wrap.append(btn, caret);
      this.tablist.append(wrap);
      this.tabNodes.push(wrap);
    } else {
      this.tablist.append(btn);
      this.tabNodes.push(btn);
    }
    return btn;
  }

  /** Build the trailing "More" disclosure button + its (initially hidden) overflow menu holding the
   *  given tabs. Selecting a menu item emits the same `workspace-navigate` the pinned tabs emit — so
   *  an overflow tab activates exactly like any other tab, the host routing unchanged. */
  private buildOverflow(overflowTabs: CaosWorkspaceTab[]): void {
    const activeInOverflow = overflowTabs.some((t) => t.active);

    const more = document.createElement('button');
    more.type = 'button';
    more.className = activeInOverflow ? 'more is-active' : 'more';
    more.setAttribute('part', 'tab');
    more.setAttribute('aria-haspopup', 'menu');
    more.setAttribute('aria-expanded', 'false');
    more.setAttribute('aria-label', 'More tabs');
    more.title = 'More tabs';
    const moreLabel = document.createElement('span');
    moreLabel.textContent = 'More';
    more.append(moreLabel, makeIcon('chevron'));
    more.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMenu();
    });
    more.addEventListener('keydown', (e) => this.onMoreKeydown(e));
    this.tablist.append(more);
    this.tabNodes.push(more);
    this.moreBtn = more;

    const menu = document.createElement('div');
    menu.className = 'more-menu';
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', 'More tabs');
    menu.hidden = true;
    menu.addEventListener('keydown', (e) => this.onMenuKeydown(e));
    this.menuItemEls = overflowTabs.map((t) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'more-item';
      item.setAttribute('role', 'menuitem');
      item.tabIndex = -1;
      item.dataset.id = t.id;
      if (t.active) item.setAttribute('aria-current', 'true');
      const dot = document.createElement('span');
      dot.className = 'dot';
      dot.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.textContent = t.label;
      item.append(dot, label);
      item.addEventListener('click', () => {
        this.closeMenu(false);
        this.emit(t.openInNewTab ? 'workspace-open-new-tab' : 'workspace-navigate', {
          href: t.href,
          id: t.id,
        });
      });
      menu.append(item);
      return item;
    });
    // Menu lives in the shadow root (not the overflow-clipped tablist) so it is never truncated.
    this.root.append(menu);
    this.moreMenu = menu;
  }

  /** Toggle the overflow menu open/closed. */
  private toggleMenu(): void {
    if (this.menuOpen) this.closeMenu(true);
    else this.openMenu();
  }

  /** Open the overflow menu, position it against the More button, and move focus to the active item
   *  (else the first). Installs light-dismiss listeners. */
  private openMenu(): void {
    if (!this.moreBtn || !this.moreMenu || this.menuOpen) return;
    this.menuOpen = true;
    this.moreBtn.setAttribute('aria-expanded', 'true');
    this.moreMenu.hidden = false;
    this.positionMenu();
    // Defer listener install so the opening click doesn't immediately dismiss.
    setTimeout(() => {
      if (!this.menuOpen) return;
      document.addEventListener('pointerdown', this.onDocPointer, true);
      window.addEventListener('resize', this.onWinDismiss);
      window.addEventListener('scroll', this.onWinDismiss, true);
    }, 0);
    const current = this.menuItemEls.find((el) => el.getAttribute('aria-current') === 'true');
    (current ?? this.menuItemEls[0])?.focus();
  }

  /** Close the overflow menu; when `returnFocus`, move focus back to the More button. */
  private closeMenu(returnFocus: boolean): void {
    if (!this.menuOpen) {
      if (this.moreMenu) this.moreMenu.hidden = true;
      return;
    }
    this.menuOpen = false;
    document.removeEventListener('pointerdown', this.onDocPointer, true);
    window.removeEventListener('resize', this.onWinDismiss);
    window.removeEventListener('scroll', this.onWinDismiss, true);
    if (this.moreMenu) this.moreMenu.hidden = true;
    if (this.moreBtn) this.moreBtn.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.moreBtn?.focus();
  }

  /** Pin the fixed-position menu under the More button's right edge (right-aligned like Salesforce). */
  private positionMenu(): void {
    if (!this.moreBtn || !this.moreMenu) return;
    // Right-aligned to the button, above it when there is no room below, and kept inside the
    // visible viewport (CAOS-659).
    anchorPanel(this.moreMenu, this.moreBtn, { side: 'bottom', align: 'end' });
  }

  /** Keyboard on the More button: Down/Enter/Space open the menu (focusing the first item). */
  private onMoreKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      this.openMenu();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      this.openMenu();
      this.menuItemEls[this.menuItemEls.length - 1]?.focus();
    }
  }

  /** Keyboard within the overflow menu: Up/Down roam, Home/End jump, Escape closes, Tab closes. */
  private onMenuKeydown(e: KeyboardEvent): void {
    const items = this.menuItemEls;
    if (items.length === 0) return;
    const active = (this.root.activeElement ?? document.activeElement) as HTMLElement | null;
    let idx = items.findIndex((el) => el === active);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        idx = idx < 0 ? 0 : (idx + 1) % items.length;
        items[idx]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        idx = idx <= 0 ? items.length - 1 : idx - 1;
        items[idx]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        this.closeMenu(true);
        break;
      case 'Tab':
        this.closeMenu(false);
        break;
    }
  }

  /** The horizontal roving set: the anchor, then the pinned tabs, then the "More" disclosure (when a
   *  split is declared). The More button joins the same Arrow-key roving as a tab so the overflow set
   *  is fully keyboard-reachable. */
  private rovingSet(): HTMLButtonElement[] {
    // CAOS-768 — the pinned tabs sit between the anchor and the app tabs in the DOM, so they sit
    // there here too: arrow-key order that disagreed with reading order would be its own defect.
    const inRow = [this.anchor, ...this.homeTabEls, ...this.tabEls];
    return this.moreBtn ? [...inRow, this.moreBtn] : inRow;
  }

  /** Roving tabindex — only the active tab (else the More button when an overflow tab is active, else
   *  the anchor) is tabbable. */
  private reflectRovingTabindex(): void {
    const all = this.rovingSet();
    let activeIdx = all.findIndex((el) => el.getAttribute('aria-selected') === 'true');
    // The active surface can live in the overflow set — then the More button carries the active tint.
    if (activeIdx < 0 && this.moreBtn?.classList.contains('is-active')) activeIdx = all.length - 1;
    const tabbable = activeIdx >= 0 ? activeIdx : 0;
    all.forEach((el, i) => {
      el.tabIndex = i === tabbable ? 0 : -1;
    });
  }

  private onKeydown(e: KeyboardEvent): void {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    // Vertical arrows inside the More button open the menu — don't also roam the strip.
    if (this.moreBtn && (this.root.activeElement === this.moreBtn) && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      return;
    }
    const all = this.rovingSet();
    if (all.length === 0) return;
    e.preventDefault();
    const active = (this.root.activeElement ?? document.activeElement) as HTMLElement | null;
    let idx = all.findIndex((el) => el === active);
    if (idx < 0) idx = all.findIndex((el) => el.tabIndex === 0);
    if (idx < 0) idx = 0;
    const last = all.length - 1;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        idx = idx >= last ? 0 : idx + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        idx = idx <= 0 ? last : idx - 1;
        break;
      case 'Home':
        idx = 0;
        break;
      case 'End':
        idx = last;
        break;
    }
    all[idx]?.focus();
  }
}

define('caos-workspace-tabs', CaosWorkspaceTabs);

declare global {
  interface HTMLElementTagNameMap {
    'caos-workspace-tabs': CaosWorkspaceTabs;
  }
}
